import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { fetchProfile } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { gradeBand } from "../lib/scoreUtils";
import { ArrowLeft, Loader2, GraduationCap, TrendingUp, Award, Target, FileText } from "lucide-react";

const Stat = ({ label, value, sub, testId }) => (
  <div className="bg-card p-5" data-testid={testId}>
    <p className="overline text-muted-foreground">{label}</p>
    <p className="font-mono text-3xl font-semibold tabular-nums mt-1 text-oxford">{value}</p>
    {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
  </div>
);

const niceSkill = (k) => (k ? k.replace(/_/g, " ") : "—");

export default function Profile() {
  const navigate = useNavigate();
  const { user, setAuthOpen } = useAuth();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    if (user === null) return;            // still checking
    if (!user) { setErr(true); return; }  // anon
    fetchProfile().then(setP).catch(() => setErr(true));
  }, [user]);

  if (user === null) return <div className="h-screen flex items-center justify-center text-muted-foreground"><Loader2 className="animate-spin" /></div>;

  if (!user) return (
    <div className="h-screen flex flex-col items-center justify-center gap-4 px-8 text-center">
      <GraduationCap size={40} className="text-oxford" />
      <h1 className="font-serif text-4xl tracking-tight">Your Academic Profile</h1>
      <p className="text-muted-foreground max-w-md">Sign in to track your predicted grades, see your strongest and weakest skills, and watch your writing improve over time.</p>
      <div className="flex gap-3">
        <button onClick={() => navigate("/")} className="rounded-none border border-border px-5 py-2.5 text-sm" data-testid="profile-back-btn">Back</button>
        <button onClick={() => setAuthOpen(true)} className="rounded-none bg-oxford text-white px-5 py-2.5 text-sm" data-testid="profile-signin-btn">Sign in</button>
      </div>
    </div>
  );

  if (err) return <div className="h-screen flex items-center justify-center text-muted-foreground">Could not load profile.</div>;
  if (!p) return <div className="h-screen flex items-center justify-center text-muted-foreground"><Loader2 className="animate-spin" /></div>;

  const chartData = (p.grade_history || []).map((h, i) => ({ name: `#${i + 1}`, grade: h.grade, file: h.filename }));

  return (
    <div className="min-h-screen bg-background" data-testid="profile-page">
      <div className="border-b border-border bg-card px-8 py-4 flex items-center justify-between">
        <button onClick={() => navigate("/")} className="inline-flex items-center gap-2 text-sm text-oxford hover:underline" data-testid="profile-back-btn">
          <ArrowLeft size={16} /> Back to examiner
        </button>
        <span className="text-sm text-muted-foreground">{p.user?.name} · {p.user?.email}</span>
      </div>

      <div className="max-w-5xl mx-auto px-8 py-10">
        <p className="overline text-oxford mb-2">Academic Profile</p>
        <h1 className="font-serif text-5xl tracking-tight mb-1">Your progress so far</h1>
        <p className="text-muted-foreground mb-8">Across {p.assignments_examined} examined assignment{p.assignments_examined === 1 ? "" : "s"}.</p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border border border-border mb-px">
          <Stat testId="stat-average" label="Average grade" value={`${p.average_grade}%`} sub={gradeBand(p.average_grade)} />
          <Stat testId="stat-highest" label="Highest predicted" value={`${p.highest_grade}%`} />
          <Stat testId="stat-examined" label="Assignments" value={p.assignments_examined} sub="examined" />
          <Stat testId="stat-prediction" label="Degree prediction" value={p.degree_prediction === "—" ? "—" : ""} sub={p.degree_prediction !== "—" ? p.degree_prediction : "more data needed"} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-border border border-border border-t-0 mb-10">
          <div className="bg-card p-5" data-testid="profile-strongest">
            <p className="overline text-muted-foreground flex items-center gap-1.5"><Award size={13} /> Strongest skill</p>
            <p className="font-serif text-2xl capitalize mt-1 text-[#15803D]">{niceSkill(p.strongest_skill)}</p>
          </div>
          <div className="bg-card p-5" data-testid="profile-weakest">
            <p className="overline text-muted-foreground flex items-center gap-1.5"><Target size={13} /> Focus area</p>
            <p className="font-serif text-2xl capitalize mt-1 text-[#B45309]">{niceSkill(p.weakest_skill)}</p>
          </div>
          <div className="bg-card p-5" data-testid="profile-improved">
            <p className="overline text-muted-foreground flex items-center gap-1.5"><TrendingUp size={13} /> Most improved</p>
            <p className="font-serif text-2xl capitalize mt-1">{p.most_improved_skill ? niceSkill(p.most_improved_skill) : "—"}{p.most_improved_delta ? <span className="text-base text-[#15803D] ml-1">+{p.most_improved_delta}%</span> : ""}</p>
          </div>
        </div>

        <div className="border border-border bg-card p-6">
          <p className="overline text-muted-foreground mb-4">Predicted grade over time</p>
          {chartData.length < 2 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">Examine a few more assignments to see your trend line.</p>
          ) : (
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <LineChart data={chartData} margin={{ top: 5, right: 10, bottom: 5, left: -20 }}>
                  <CartesianGrid stroke="#eee" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Line type="monotone" dataKey="grade" stroke="#1A365D" strokeWidth={2} dot={{ r: 4, fill: "#1A365D" }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <p className="text-xs text-muted-foreground mt-4 flex items-center gap-1.5">
          <FileText size={12} /> Coming soon: enter your actual marks to see how accurately we predicted them.
        </p>
      </div>
    </div>
  );
}
